'use client';

import React, { useState, useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Briefcase, ArrowRight, Bot, User, Send, Sparkles, Award, Target, ChevronRight, DollarSign } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import { toast } from 'sonner';

const careerPaths = [
  { title: 'Software Engineer', stream: 'Engineering', entry: '₹8–15 LPA', mid: '₹20–40 LPA', senior: '₹50–1.5 Cr', growth: '+28%', icon: '💻', skills: ['DSA', 'System Design', 'Cloud', 'React/Node'], topColleges: ['IIT Delhi', 'BITS Pilani', 'NIT Trichy'] },
  { title: 'Data Scientist', stream: 'Engineering', entry: '₹10–18 LPA', mid: '₹25–50 LPA', senior: '₹60–2 Cr', growth: '+35%', icon: '📊', skills: ['Python', 'ML/AI', 'Statistics', 'SQL'], topColleges: ['IIT Bombay', 'IISc', 'IIT Madras'] },
  { title: 'Investment Banker', stream: 'Management', entry: '₹12–20 LPA', mid: '₹30–60 LPA', senior: '₹1–5 Cr', growth: '+18%', icon: '🏦', skills: ['Finance', 'Valuation', 'Excel', 'CFA'], topColleges: ['IIM Ahmedabad', 'IIM Calcutta', 'XLRI'] },
  { title: 'Doctor (MBBS+MD)', stream: 'Medical', entry: '₹8–15 LPA', mid: '₹20–50 LPA', senior: '₹50L–2 Cr', growth: '+22%', icon: '🩺', skills: ['Clinical Skills', 'Research', 'Specialisation'], topColleges: ['AIIMS Delhi', 'CMC Vellore', 'JIPMER'] },
  { title: 'Corporate Lawyer', stream: 'Law', entry: '₹6–12 LPA', mid: '₹15–35 LPA', senior: '₹40L–1.5 Cr', growth: '+15%', icon: '⚖️', skills: ['Contract Law', 'M&A', 'Litigation', 'Research'], topColleges: ['NLSIU', 'NLU Delhi', 'Symbiosis Law'] },
  { title: 'Product Manager', stream: 'Management', entry: '₹15–25 LPA', mid: '₹30–60 LPA', senior: '₹70L–2.5 Cr', growth: '+40%', icon: '🚀', skills: ['Strategy', 'Analytics', 'UX', 'Agile'], topColleges: ['IIM Bangalore', 'ISB', 'IIT + MBA'] },
];

const roadmapSteps = [
  { phase: 'Class 11–12', title: 'Foundation', desc: 'Choose stream, prepare for entrance exams, build fundamentals', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { phase: 'UG (3–4 yrs)', title: 'Degree & Skills', desc: 'Core academics, internships, projects, certifications, networking', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { phase: 'Entry Level', title: 'First Job', desc: 'Campus placements or off-campus, build 2–3 years of experience', color: 'bg-green-100 text-green-700 border-green-200' },
  { phase: 'PG / MBA', title: 'Specialise', desc: 'Optional: MBA/MS/MTech to accelerate career growth and salary', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { phase: 'Mid Career', title: 'Leadership', desc: 'Senior roles, team management, domain expertise, global exposure', color: 'bg-rose-100 text-rose-700 border-rose-200' },
];

const SYSTEM_PROMPT = `You are an expert AI Career Advisor for Indian students on All About Campus — India's leading college discovery platform. Your role is to provide personalized, accurate, and actionable career guidance.

You specialize in:
- Career path recommendations based on student interests, stream, and academic background
- College recommendations (IITs, NITs, IIMs, AIIMS, NLUs, BITS Pilani, and other top Indian colleges)
- Entrance exam guidance (JEE Main/Advanced, NEET, CAT, CLAT, GATE, CUET, XAT, GMAT, GRE)
- Salary benchmarks and career growth projections for Indian job market (2024–2025)
- Skills and certifications required for specific careers
- Study abroad options vs Indian colleges comparison
- Scholarship and financial aid information

Guidelines:
- Be concise, friendly, and encouraging
- Use Indian context (₹ for salaries, Indian colleges, Indian job market)
- Provide specific, actionable advice with numbers and timelines
- When recommending colleges, mention NIRF rankings and placement packages
- Keep responses under 200 words unless a detailed breakdown is specifically requested
- Use bullet points or numbered lists for clarity when listing multiple items`;

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
}

const suggestedQuestions = [
  'What career is best for a CSE graduate?',
  'How much does an MBA from IIM pay?',
  'What skills do I need to become a Data Scientist?',
  'Is MBBS worth it in 2025?',
];

export default function CollegeToCareerPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'ai', text: "Hi! I'm your AI Career Advisor powered by ChatGPT 🚀 Tell me your interests, stream, or ask about any career path — I'll give you personalised guidance from college to your dream job!" }
  ]);
  const [input, setInput] = useState('');
  const [activeCareer, setActiveCareer] = useState(careerPaths[0]);
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: string; content: string }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { response, isLoading, error, sendMessage: sendAIMessage } = useChat('OPEN_AI', 'gpt-4o', true);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Track previous response to detect new completed responses
  const prevResponseRef = useRef('');

  useEffect(() => {
    if (response && !isLoading && response !== prevResponseRef.current) {
      prevResponseRef.current = response;
      setMessages(prev => {
        // Replace the streaming placeholder if it exists, otherwise add new message
        const last = prev[prev.length - 1];
        if (last && last.role === 'ai' && last.text === '...') {
          return [...prev.slice(0, -1), { role: 'ai', text: response }];
        }
        // Update the last ai message with the full response
        if (last && last.role === 'ai') {
          return [...prev.slice(0, -1), { role: 'ai', text: response }];
        }
        return [...prev, { role: 'ai', text: response }];
      });
      setConversationHistory(prev => [...prev, { role: 'assistant', content: response }]);
    }
  }, [response, isLoading]);

  // Live streaming update
  useEffect(() => {
    if (isLoading && response) {
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'ai') {
          return [...prev.slice(0, -1), { role: 'ai', text: response }];
        }
        return prev;
      });
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = () => {
    if (!input.trim() || isLoading) return;
    const userText = input.trim();
    setInput('');

    setMessages(prev => [...prev, { role: 'user', text: userText }, { role: 'ai', text: '...' }]);

    const newHistory = [...conversationHistory, { role: 'user', content: userText }];
    setConversationHistory(newHistory);

    const apiMessages = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...newHistory,
    ];

    sendAIMessage(apiMessages, { max_completion_tokens: 500 });
  };

  const handleSuggestedQuestion = (q: string) => {
    setInput(q);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">

        {/* Hero */}
        <section className="relative bg-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary blur-3xl -translate-y-1/2 translate-x-1/4" />
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-14 lg:py-18">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm text-slate-200 mb-5 border border-white/20">
                <Briefcase size={14} className="text-primary" />
                <span>College to Career — Your Complete Roadmap</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4">
                Turn Your Degree Into<br />
                <span className="text-primary">Your Dream Career</span>
              </h1>
              <p className="text-slate-300 text-lg max-w-xl">
                Explore career paths, salary benchmarks, required skills, and get AI-powered guidance to bridge the gap between college and career.
              </p>
            </div>
          </div>
        </section>

        {/* Career Explorer */}
        <section className="py-14 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground">Explore Career Paths</h2>
            <p className="text-muted-foreground text-sm mt-1">Click any career to see detailed salary, skills, and college recommendations</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Career List */}
            <div className="space-y-3">
              {careerPaths.map((career) => (
                <button
                  key={career.title}
                  onClick={() => setActiveCareer(career)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-all duration-200 ${activeCareer.title === career.title ? 'border-primary bg-accent' : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50'}`}
                >
                  <span className="text-2xl">{career.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-foreground">{career.title}</p>
                    <p className="text-xs text-muted-foreground">{career.stream}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-bold text-green-600">{career.growth}</p>
                    <p className="text-xs text-muted-foreground">growth</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Career Detail */}
            <div className="lg:col-span-2 bg-card rounded-2xl border border-border overflow-hidden">
              <div className="bg-gradient-to-r from-secondary to-primary/80 p-6">
                <div className="flex items-center gap-4">
                  <span className="text-5xl">{activeCareer.icon}</span>
                  <div>
                    <h3 className="text-2xl font-extrabold text-white">{activeCareer.title}</h3>
                    <p className="text-white/70">{activeCareer.stream} Stream · {activeCareer.growth} job growth</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Salary Progression */}
                <div>
                  <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                    <DollarSign size={16} className="text-primary" /> Salary Progression
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {[['Entry Level', activeCareer.entry, 'bg-green-50 text-green-700 border-green-200'], ['Mid Career', activeCareer.mid, 'bg-blue-50 text-blue-700 border-blue-200'], ['Senior Level', activeCareer.senior, 'bg-purple-50 text-purple-700 border-purple-200']].map(([label, salary, color]) => (
                      <div key={label} className={`rounded-xl border p-3 text-center ${color}`}>
                        <p className="font-extrabold text-sm">{salary}</p>
                        <p className="text-xs opacity-70 mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Skills */}
                <div>
                  <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                    <Target size={16} className="text-primary" /> Key Skills Required
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {activeCareer.skills.map((skill) => (
                      <span key={skill} className="px-3 py-1.5 bg-accent text-primary rounded-full text-sm font-semibold border border-primary/20">{skill}</span>
                    ))}
                  </div>
                </div>

                {/* Top Colleges */}
                <div>
                  <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                    <Award size={16} className="text-primary" /> Top Colleges for This Career
                  </h4>
                  <div className="space-y-2">
                    {activeCareer.topColleges.map((college, i) => (
                      <div key={college} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : 'bg-amber-700'}`}>
                          #{i + 1}
                        </div>
                        <span className="font-semibold text-sm text-foreground">{college}</span>
                        <ChevronRight size={14} className="text-muted-foreground ml-auto" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section className="py-14 bg-muted/50">
          <div className="px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-foreground">Your Career Roadmap</h2>
              <p className="text-muted-foreground text-sm mt-1">A step-by-step guide from school to senior career</p>
            </div>
            <div className="flex flex-col md:flex-row gap-4 items-stretch">
              {roadmapSteps.map((step, i) => (
                <div key={step.phase} className="flex-1 relative">
                  <div className={`h-full rounded-2xl border-2 p-5 ${step.color}`}>
                    <div className="text-xs font-bold opacity-60 mb-1">{step.phase}</div>
                    <h3 className="font-extrabold text-base mb-2">{step.title}</h3>
                    <p className="text-xs leading-relaxed opacity-80">{step.desc}</p>
                  </div>
                  {i < roadmapSteps.length - 1 && (
                    <div className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-card border-2 border-border items-center justify-center">
                      <ArrowRight size={12} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI Career Advisor */}
        <section className="py-14 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-1.5 text-sm text-primary mb-5">
                <Sparkles size={14} />
                <span>AI Career Advisor — Powered by ChatGPT</span>
              </div>
              <h2 className="text-3xl font-extrabold text-foreground mb-4">Get Personalised Career Guidance</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Our AI advisor is powered by ChatGPT and analyses your interests, academic background, and goals to recommend the best career path and colleges for you.
              </p>
              <div className="space-y-3">
                {suggestedQuestions.map((q) => (
                  <button key={q} onClick={() => handleSuggestedQuestion(q)} className="w-full text-left px-4 py-3 rounded-xl border border-border bg-card hover:border-primary hover:bg-accent text-sm font-semibold text-foreground transition-all duration-150">
                    💬 {q}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-card">
              <div className="bg-secondary px-5 py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                  <Bot size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">AI Career Advisor</p>
                  <p className="text-slate-400 text-xs flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                    Powered by ChatGPT (GPT-4o)
                  </p>
                </div>
              </div>
              <div className="h-80 overflow-y-auto p-4 space-y-3 scrollbar-thin">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'ai' ? 'bg-primary' : 'bg-secondary'}`}>
                      {msg.role === 'ai' ? <Bot size={14} className="text-white" /> : <User size={14} className="text-white" />}
                    </div>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${msg.role === 'ai' ? 'bg-muted text-foreground rounded-tl-none' : 'bg-primary text-white rounded-tr-none'}`}>
                      {msg.text === '...' ? (
                        <div className="flex gap-1 py-0.5">
                          <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      ) : msg.text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Ask about careers, salaries, skills..."
                    className="input-base flex-1 text-sm"
                    disabled={isLoading}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={isLoading || !input.trim()}
                    className="btn-primary px-4 py-2 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
